import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Progress } from '@/components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { 
  X, 
  BarChart4, 
  Calendar, 
  Award,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  BookOpen,
  Target,
  MessageSquare,
  Layers,
  FolderOpen,
  NotebookPen
} from 'lucide-react'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'

// Import modular components
import MemberKPAPanel from './MemberKPAPanel'
import { LeaderObservationsPanel } from './LeaderObservationsPanel'
import EvidencePortfolio from '../EvidencePortfolio'
import { visibleForFormalPeriod, buildCarriedMidYearEvidence } from '@/lib/evidenceReleaseFilter'
import ObjectiveConfigurationDialog from './ObjectiveConfigurationDialog'
import { VideoHelpButton } from '@/components/guidance/VideoHelpButton'
import StakeholderFeedbackSystem from '../feedback/StakeholderFeedbackSystem'
import StrategicCascadeViewer from './StrategicCascadeViewer'
import { EmployeeIDPView } from '../development/EmployeeIDPView'
import PerformanceOnePager from '../PerformanceOnePager'
import { ViewModeToggle } from '@/components/journey/ViewModeToggle'
import { useJourneyViewMode } from '@/lib/journeyViewMode'
import { performanceService, type RoleKPI } from '@/services/api/performanceService'
import { getCurrentReviewPeriod as resolveReviewPeriod } from '@/lib/reviewPeriod'
import { useReviewCycleConfig } from '@/hooks/useReviewCycleConfig'
import { useObjectiveConfigHandlers } from '@/hooks/useObjectiveConfigHandlers'
import { reviewService, type ReviewSessionData } from '@/services/api/reviewService'
import type { TeamMember } from '@/services/api/managerService'

// Import types
import { EnhancedEvidenceEntry, JITCheckIn, KPAData } from '@/types/evidence'
import { getKpaDescription } from '@/utils/performance/kpaDescriptions'
import { CollaborationSession } from '@/types/objectives'

// Import enterprise progress calculation
import { calculateProgress } from '@/utils/kpiProgress'
import type { MeasurementDirection, MeasurementMode } from '@/types/kpi'

// Mock data generator for development/fallback
function getMockRoleKPIs(_role: string, memberId: string): RoleKPI[] {
  // Generate realistic mock KPIs based on role - matches API structure
  const baseKPIs: RoleKPI[] = [
    {
      id: 'kpi-teach-qual-1',
      name: 'Teaching Quality Score',
      description: 'Student feedback rating for teaching effectiveness',
      target_value: 4.2,
      current_value: 4.5,
      unit: '/5.0',
      status: 'on-track',
      frequency: 'quarterly',
      role_type: 'lecturer',
      functional_area: 'Academic Affairs',
      campus: 'Johannesburg Central',
      owner_id: memberId,
      owner: memberId,
      cascade_to_objective_id: null,
      cascade_to_kpa_id: null,
      cascade_to_pillar_id: null,
      kpa_id: 1,
      pillar_id: 1,
      strategic_objective_id: 1,
      kpa_name: 'Teaching Excellence',
      pillar_name: 'Academic Excellence',
      strategic_objective_name: 'Deliver World-Class Education',
      individual_objective_id: 101,
      primary_initiative_id: null,
      category: 'Quality',
      priority: 'high',
      progress_percentage: 88,
      midyear_progress: 45,
      yearend_progress: 88,
      trend: 'up',
      start_date: '2024-01-01',
      end_date: '2024-12-31',
      is_active: true,
      milestones: [],
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-06-01T00:00:00Z',
      individual_objective: {
        id: 101,
        title: 'Maintain High Teaching Standards',
        description: 'Achieve consistent student satisfaction ratings',
        role_type: 'lecturer',
        owner: memberId,
        functional_area: 'Academic Affairs',
        status: 'on-track',
        progress: 88,
        start_date: '2024-01-01',
        end_date: '2024-12-31',
        strategic_objective_id: 1,
        kpa_id: 1,
        pillar_id: 1
      },
      primary_initiative: null
    },
    {
      id: 'kpi-pass-rate-1',
      name: 'Pass Rate',
      description: 'Percentage of students passing the module',
      target_value: 85,
      current_value: 92,
      unit: '%',
      status: 'on-track',
      frequency: 'quarterly',
      role_type: 'lecturer',
      functional_area: 'Academic Affairs',
      campus: 'Johannesburg Central',
      owner_id: memberId,
      owner: memberId,
      cascade_to_objective_id: null,
      cascade_to_kpa_id: null,
      cascade_to_pillar_id: null,
      kpa_id: 1,
      pillar_id: 1,
      strategic_objective_id: 1,
      kpa_name: 'Teaching Excellence',
      pillar_name: 'Academic Excellence',
      strategic_objective_name: 'Deliver World-Class Education',
      individual_objective_id: 102,
      primary_initiative_id: null,
      category: 'Performance',
      priority: 'high',
      progress_percentage: 108,
      midyear_progress: 50,
      yearend_progress: 108,
      trend: 'up',
      start_date: '2024-01-01',
      end_date: '2024-12-31',
      is_active: true,
      milestones: [],
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-06-01T00:00:00Z',
      individual_objective: {
        id: 102,
        title: 'Improve Student Success Rate',
        description: 'Support students to achieve passing grades',
        role_type: 'lecturer',
        owner: memberId,
        functional_area: 'Academic Affairs',
        status: 'on-track',
        progress: 95,
        start_date: '2024-01-01',
        end_date: '2024-12-31',
        strategic_objective_id: 1,
        kpa_id: 1,
        pillar_id: 1
      },
      primary_initiative: null
    },
    {
      id: 'kpi-research-1',
      name: 'Research Publications',
      description: 'Number of peer-reviewed articles published',
      target_value: 2,
      current_value: 1,
      unit: ' articles',
      status: 'at-risk',
      frequency: 'annual',
      role_type: 'lecturer',
      functional_area: 'Research',
      campus: 'Johannesburg Central',
      owner_id: memberId,
      owner: memberId,
      cascade_to_objective_id: null,
      cascade_to_kpa_id: null,
      cascade_to_pillar_id: null,
      kpa_id: 2,
      pillar_id: 1,
      strategic_objective_id: 2,
      kpa_name: 'Research & Innovation',
      pillar_name: 'Academic Excellence',
      strategic_objective_name: 'Advance Research Output',
      individual_objective_id: 201,
      primary_initiative_id: null,
      category: 'Research',
      priority: 'medium',
      progress_percentage: 72,
      midyear_progress: 40,
      yearend_progress: 72,
      trend: 'stable',
      start_date: '2024-01-01',
      end_date: '2024-12-31',
      is_active: true,
      milestones: [],
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-06-01T00:00:00Z',
      individual_objective: {
        id: 201,
        title: 'Publish Research Papers',
        description: 'Contribute to academic research output',
        role_type: 'lecturer',
        owner: memberId,
        functional_area: 'Research',
        status: 'at-risk',
        progress: 50,
        start_date: '2024-01-01',
        end_date: '2024-12-31',
        strategic_objective_id: 2,
        kpa_id: 2,
        pillar_id: 1
      },
      primary_initiative: null
    },
    {
      id: 'kpi-student-support-1',
      name: 'Student Consultation Hours',
      description: 'Hours dedicated to student consultation and support',
      target_value: 40,
      current_value: 38,
      unit: ' hours',
      status: 'on-track',
      frequency: 'quarterly',
      role_type: 'lecturer',
      functional_area: 'Student Support',
      campus: 'Johannesburg Central',
      owner_id: memberId,
      owner: memberId,
      cascade_to_objective_id: null,
      cascade_to_kpa_id: null,
      cascade_to_pillar_id: null,
      kpa_id: 3,
      pillar_id: 2,
      strategic_objective_id: 3,
      kpa_name: 'Student Experience',
      pillar_name: 'Student Success',
      strategic_objective_name: 'Enhance Student Experience',
      individual_objective_id: 301,
      primary_initiative_id: null,
      category: 'Support',
      priority: 'high',
      progress_percentage: 95,
      midyear_progress: 48,
      yearend_progress: 95,
      trend: 'stable',
      start_date: '2024-01-01',
      end_date: '2024-12-31',
      is_active: true,
      milestones: [],
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-06-01T00:00:00Z',
      individual_objective: {
        id: 301,
        title: 'Provide Student Support',
        description: 'Be available for student consultations',
        role_type: 'lecturer',
        owner: memberId,
        functional_area: 'Student Support',
        status: 'on-track',
        progress: 95,
        start_date: '2024-01-01',
        end_date: '2024-12-31',
        strategic_objective_id: 3,
        kpa_id: 3,
        pillar_id: 2
      },
      primary_initiative: null
    },
    {
      id: 'kpi-prof-dev-1',
      name: 'Professional Development Hours',
      description: 'Hours completed in professional development activities',
      target_value: 35,
      current_value: 42,
      unit: ' hours',
      status: 'on-track',
      frequency: 'annual',
      role_type: 'lecturer',
      functional_area: 'Professional Development',
      campus: 'Johannesburg Central',
      owner_id: memberId,
      owner: memberId,
      cascade_to_objective_id: null,
      cascade_to_kpa_id: null,
      cascade_to_pillar_id: null,
      kpa_id: 4,
      pillar_id: 3,
      strategic_objective_id: 4,
      kpa_name: 'Professional Growth',
      pillar_name: 'Organisational Excellence',
      strategic_objective_name: 'Develop Faculty Capability',
      individual_objective_id: 401,
      primary_initiative_id: null,
      category: 'Development',
      priority: 'medium',
      progress_percentage: 120,
      midyear_progress: 60,
      yearend_progress: 120,
      trend: 'up',
      start_date: '2024-01-01',
      end_date: '2024-12-31',
      is_active: true,
      milestones: [],
      created_at: '2024-01-01T00:00:00Z',
      updated_at: '2024-06-01T00:00:00Z',
      individual_objective: {
        id: 401,
        title: 'Complete Professional Development',
        description: 'Engage in continuous professional development',
        role_type: 'lecturer',
        owner: memberId,
        functional_area: 'Professional Development',
        status: 'on-track',
        progress: 100,
        start_date: '2024-01-01',
        end_date: '2024-12-31',
        strategic_objective_id: 4,
        kpa_id: 4,
        pillar_id: 3
      },
      primary_initiative: null
    }
  ]

  return baseKPIs
}

interface MemberDetailsDialogProps {
  open: boolean
  onClose: () => void
  memberName: string
  memberId: string
  currentUserId?: string // The logged-in manager's ID (for permission checks)
  onConductReview: () => void
  onScheduleReview: () => void
  onViewIDP: () => void
  
  // Full enriched team member data from API
  memberData?: TeamMember
  
  // Enhanced props for full functionality
  memberKPAs?: KPAData[]
  evidence?: EnhancedEvidenceEntry[]
  
  // Evidence handlers
  onAddEvidence?: (evidence: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>) => Promise<EnhancedEvidenceEntry | undefined> | void
  onUpdateEvidence?: (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => void
  onUpdateEvidenceClassification?: (evidenceId: string, classification: EnhancedEvidenceEntry['classification']) => void
  onDeleteEvidence?: (evidenceId: string) => void
  onUploadDocument?: (file: File, evidenceData: Partial<EnhancedEvidenceEntry>) => Promise<void>
  onRefreshEvidence?: () => Promise<void>
  
  // KPA workspace handler
  onViewKPAWorkspace?: (kpa: KPAData) => void
  
  // Individual Objective handlers (for KPA panels)
  onCreateObjective?: (objective: any) => void
  onUpdateObjective?: (objectiveId: number, updates: any) => void
  onDeleteObjective?: (objectiveId: number) => void
  onViewObjectiveDetails?: (objective: any) => void
  onApproveObjective?: (objectiveId: number) => void
  onRejectObjective?: (objectiveId: number, reason: string) => void
  
  // Feedback handlers
  feedbackRequests?: any[]
  onCreateFeedbackRequest?: (request: any) => void
  onRefreshFeedback?: () => void
  
  // Strategic cascade data
  memberCascadeData?: any[]
  
  // Member role for API data loading
  memberRole?: string
  
  // Review sessions for determining review status/completion
  memberReviewSessions?: ReviewSessionData[]
  
  // Trigger for refreshing internal data (increment to force reload)
  refreshTrigger?: number
}

// Mock data interface - TODO: Replace with actual API data
interface MemberDetails {
  id: string
  name: string
  role: string
  campus: string
  department: string
  employmentType: 'Permanent' | 'FTC' | 'IC'
  tenureYears: number
  performance: {
    current: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr' | null
    trend: 'improving' | 'maintaining' | 'declining' | null
    lastRating: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr' | null
  }
  kpas: {
    id: string
    name: string
    progress: number
    objectivesCount: number
    kpisCount: number
    evidenceCount: number
    status: 'on-track' | 'at-risk' | 'off-track'
  }[]
  nextReview: {
    type: string
    date: string
    status: 'scheduled' | 'pending-scheduling' | 'overdue'
  }
  alerts: string[]
}

export default function MemberDetailsDialog({
  open,
  onClose,
  memberName,
  memberId,
  currentUserId,
  onConductReview,
  onScheduleReview,
  onViewIDP,
  memberKPAs = [],
  evidence = [],
  onAddEvidence,
  onUpdateEvidence,
  onUpdateEvidenceClassification,
  onDeleteEvidence,
  onUploadDocument,
  onRefreshEvidence,
  onViewKPAWorkspace,
  onCreateObjective,
  onUpdateObjective,
  onDeleteObjective,
  onViewObjectiveDetails,
  onApproveObjective,
  onRejectObjective,
  feedbackRequests = [],
  onCreateFeedbackRequest,
  onRefreshFeedback,
  memberCascadeData = [],
  memberRole: propMemberRole = 'lecturer',
  memberData,
  memberReviewSessions = [],
  refreshTrigger = 0
}: MemberDetailsDialogProps) {
  // Debug: Log props on every render
  if (import.meta.env.DEV) console.log(`📥 MemberDetailsDialog received memberKPAs prop with ${memberKPAs.length} items`)
  
  // Global Simple/Detailed view mode (localStorage-backed, synced across surfaces)
  const [journeyViewMode] = useJourneyViewMode()

  // Live review-cycle boundary (HR-configurable Mid-Year → Year-End cutover)
  const reviewCycleCfg = useReviewCycleConfig()

  // Ref + ResizeObserver to measure the desktop tab bar height dynamically
  const desktopTabBarRef = useRef<HTMLDivElement>(null)
  const [tabBarHeight, setTabBarHeight] = useState(112) // sensible default for 2-row tab bar
  
  useEffect(() => {
    const el = desktopTabBarRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.height > 0) {
          setTabBarHeight(entry.contentRect.height)
        }
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [open])
  
  // Persist active tab using sessionStorage to survive re-renders and component remounts
  const tabStorageKey = `memberDialog_${memberId}_activeTab`
  const [activeTab, setActiveTab] = useState(() => {
    const stored = sessionStorage.getItem(tabStorageKey)
    return stored || 'overview'
  })
  
  // Wrap setActiveTab to also persist to sessionStorage
  const handleTabChange = useCallback((newTab: string) => {
    setActiveTab(newTab)
    sessionStorage.setItem(tabStorageKey, newTab)
  }, [tabStorageKey])
  
  const [roleKPIs, setRoleKPIs] = useState<RoleKPI[]>([])
  const [isLoadingKPIs, setIsLoadingKPIs] = useState(false)
  const [kpiError, setKpiError] = useState<string | null>(null)
  const [highlightedKPIId, setHighlightedKPIId] = useState<string | null>(null)
  const [kpiSnapshots, setKpiSnapshots] = useState<Map<string, Map<string, number>>>(new Map()) // KPA name -> (KPI ID -> snapshot value)
  
  // Objective Configuration Dialog state
  const [showObjectiveConfig, setShowObjectiveConfig] = useState(false)
  const [objectiveConfigInitialTab, setObjectiveConfigInitialTab] = useState<'pending' | 'active' | 'rejected' | 'allocate'>('active')
  
  // Review Period Filter - Global filter for all data in dialog
  // Default to current period based on the configured Mid-Year → Year-End boundary
  const getDefaultPeriod = (): 'MIDYEAR' | 'YEAREND' => {
    return resolveReviewPeriod(new Date(), reviewCycleCfg)
  }
  const [selectedReviewPeriod, setSelectedReviewPeriod] = useState<'MIDYEAR' | 'YEAREND'>(getDefaultPeriod())
  
  // Year Filter - Dynamic year selection for performance cycles
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i) // 2 years back, current, 2 years forward
  
  // System launch year - years before this are "legacy" (F&O only)
  const SYSTEM_LAUNCH_YEAR = 2026
  const isLegacyYear = selectedYear < SYSTEM_LAUNCH_YEAR
  
  // Suppress warnings for legacy props - reserved for future use
  void onConductReview
  void onScheduleReview
  void onViewIDP
  void isLoadingKPIs
  void kpiError
  
  // Normalize role type to lowercase and remove spaces
  const memberRole = propMemberRole.toLowerCase().replace(/\s+/g, '-')
  
  // Smart review period detection based on the configured Mid-Year → Year-End boundary
  const getCurrentReviewPeriod = useCallback((): 'MIDYEAR' | 'YEAREND' => {
    return resolveReviewPeriod(new Date(), reviewCycleCfg)
  }, [reviewCycleCfg])
  
  // Helper to check if a review period is actually completed based on review sessions
  const isReviewPeriodCompleted = useCallback((period: 'MIDYEAR' | 'YEAREND', year: number): boolean => {
    const reviewType = period === 'MIDYEAR' ? 'midyear' : 'yearend'
    const session = memberReviewSessions.find(
      s => s.review_type === reviewType && s.review_year === year
    )
    // Only consider completed if status is 'completed' and completed_date is set
    return session?.status === 'completed' && !!session?.completed_date
  }, [memberReviewSessions])

  // Determine if we're viewing historical data (read-only) or current data (editable)
  const isHistoricalView = useMemo(() => {
    // Past years are always historical
    if (selectedYear < currentYear) return true
    
    // Future years are always editable
    if (selectedYear > currentYear) return false
    
    // Current year - check if selected period is ACTUALLY completed (not just calendar-based)
    if (selectedYear === currentYear) {
      // Check if the review for this period has been completed
      if (isReviewPeriodCompleted(selectedReviewPeriod, selectedYear)) {
        return true
      }
    }
    
    return false
  }, [selectedYear, currentYear, selectedReviewPeriod, isReviewPeriodCompleted])
  
  // A People Leader looking at a team member records private observations.
  const canRecordObservations = !!(onAddEvidence && onDeleteEvidence && onUploadDocument &&
    currentUserId && currentUserId !== memberId)

  // Wrap onAddEvidence to inject selected review period
  const handleAddEvidenceWithPeriod = useCallback(async (evidenceData: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>): Promise<EnhancedEvidenceEntry | undefined> => {
    const targetPeriod = selectedReviewPeriod
    const periodLabel = targetPeriod === 'MIDYEAR' ? 'Mid-Year' : 'Year-End'

    // A refusal is thrown, not returned: the form then stays open and says why.
    // Returning nothing closed it as if the entry had saved (HR UAT list #6).
    if (selectedYear < currentYear) {
      throw new Error("Entries can't be added to a past year.")
    }

    // Check if the review period is actually completed (based on review session status)
    if (isReviewPeriodCompleted(targetPeriod, selectedYear)) {
      throw new Error(`The ${periodLabel} check-in is completed, so entries can't be added to it.`)
    }

    // Inject the review period into evidence
    const evidenceWithPeriod = {
      ...evidenceData,
      reviewPeriod: targetPeriod
    }

    const result = await onAddEvidence?.(evidenceWithPeriod)
    toast.success(`Entry added to ${periodLabel}`)
    return result as EnhancedEvidenceEntry | undefined
  }, [onAddEvidence, selectedReviewPeriod, selectedYear, currentYear, isReviewPeriodCompleted])
  
  // Helper function to check if evidence can be edited.
  //
  // Authoritative source of truth is the backend's `lockedForReview` flag,
  // which is set per-evidence based on:
  //   1. Explicit `portfolio_snapshot_evidence_ids` for sessions with the
  //      curate-portfolio feature
  //   2. Legacy heuristic for older completed sessions: evidence dated on
  //      or before the session's `completed_date` with matching review_period
  //
  // The previous implementation here applied a blanket period-level lock
  // (any MIDYEAR-tagged evidence in a year with a completed mid-year review
  // was locked, regardless of its evidence_date). That blanket rule
  // incorrectly blocked entries created AFTER the check-in completed —
  // visible in the UI as entries with no "In a past check-in" badge but
  // refusing to delete with a "Cannot modify entries from completed
  // MIDYEAR review period" toast. The badge and the lock are now driven by
  // the same flag.
  const canEditEvidence = useCallback((evidenceItem: EnhancedEvidenceEntry): { canEdit: boolean; reason?: string } => {
    const evidenceDate = new Date(evidenceItem.date)
    const evidenceYear = evidenceDate.getFullYear()

    // Past calendar years are still always read-only — there's no recovering
    // a 2024 entry into the 2026 review cycle.
    if (evidenceYear < currentYear) {
      return { canEdit: false, reason: 'Cannot modify entries from past years' }
    }

    // Use the granular lock state. Only fully_locked entries are
    // wholly un-editable; the partial lock states leave one period's
    // fields editable, and the per-field server-side guards in
    // update_evidence catch any attempts to mutate frozen fields.
    // Falls back to the legacy boolean for backend responses that
    // pre-date the lock_state field.
    const ls = evidenceItem.lockState
    if (ls === 'fully_locked' || (!ls && evidenceItem.lockedForReview)) {
      return {
        canEdit: false,
        reason: 'This entry is part of a finalised check-in. Use Continue at Year-End to start a follow-up.',
      }
    }

    return { canEdit: true }
  }, [currentYear])
  
  // Wrap evidence update handlers to prevent editing historical data
  const handleUpdateEvidence = useCallback((evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => {
    // Find the evidence being updated
    const evidenceItem = evidence.find(e => e.id === evidenceId)
    
    if (!evidenceItem) {
      toast.error('Entry not found')
      return
    }
    
    // Check if evidence can be edited
    const editCheck = canEditEvidence(evidenceItem)
    if (!editCheck.canEdit) {
      toast.error(editCheck.reason || 'Cannot edit this entry')
      return
    }
    
    onUpdateEvidence?.(evidenceId, updates)
  }, [onUpdateEvidence, evidence, canEditEvidence])
  
  const handleDeleteEvidence = useCallback((evidenceId: string) => {
    // Find the evidence being deleted
    const evidenceItem = evidence.find(e => e.id === evidenceId)
    
    if (!evidenceItem) {
      toast.error('Entry not found')
      return
    }
    
    // Check if evidence can be deleted (same rules as editing)
    const editCheck = canEditEvidence(evidenceItem)
    if (!editCheck.canEdit) {
      toast.error(editCheck.reason || 'Cannot delete this entry')
      return
    }
    
    onDeleteEvidence?.(evidenceId)
  }, [onDeleteEvidence, evidence, canEditEvidence])

  // Helper to get period-appropriate KPI value and progress
  // For current period: use current_value
  // For historical/completed periods: use snapshot values (midyear_progress/yearend_progress)
  const getPeriodKPIValue = useCallback((kpi: RoleKPI): { value: number; progress: number } => {
    const targetValue = kpi.target_value || 1 // Avoid division by zero
    const currentPeriod = getCurrentReviewPeriod()
    
    // Check if we're viewing historical data (completed review period or past year)
    const viewingHistorical = isHistoricalView || 
      (selectedYear === currentYear && selectedReviewPeriod !== currentPeriod)
    
    if (viewingHistorical) {
      // Use snapshot values for historical/completed periods
      if (selectedReviewPeriod === 'MIDYEAR') {
        const midyearProgress = kpi.midyear_progress || 0
        // midyear_progress is already a percentage (0-100)
        // Calculate the equivalent current_value from the progress
        const equivalentValue = (midyearProgress / 100) * targetValue
        return { value: equivalentValue, progress: midyearProgress }
      } else {
        const yearendProgress = kpi.yearend_progress || 0
        const equivalentValue = (yearendProgress / 100) * targetValue
        return { value: equivalentValue, progress: yearendProgress }
      }
    }
    
    // For current period being worked on, use live current_value
    const currentValue = kpi.current_value || 0
    const progress = targetValue > 0 ? (currentValue / targetValue) * 100 : 0
    return { value: currentValue, progress }
  }, [getCurrentReviewPeriod, isHistoricalView, selectedReviewPeriod, selectedYear, currentYear])

  // Handler to navigate to a specific KPI within its KPA
  const handleNavigateToKPI = (kpiId: string) => {
    // Find which KPA contains this KPI
    const kpaWithKPI = displayKPAs.find(kpa => 
      kpa.underlyingKPIs.some(k => k.id === kpiId)
    )

    if (kpaWithKPI) {
      // Switch to the KPA tab
      handleTabChange(`kpa-${kpaWithKPI.id}`)
      // Highlight the KPI temporarily
      setHighlightedKPIId(kpiId)
      // Clear highlight after 3 seconds
      setTimeout(() => setHighlightedKPIId(null), 3000)
    }
  }

  // Handler to open ObjectiveConfigurationDialog
  const handleOpenObjectiveConfig = useCallback((initialTab?: 'pending' | 'active' | 'rejected' | 'allocate') => {
    setObjectiveConfigInitialTab(initialTab || 'pending')
    setShowObjectiveConfig(true)
  }, [])

  // Use centralized hook for objective configuration handlers
  // This ensures consistent collaborative session management across all components
  const {
    collaborativeSessions,
    canApproveObjectives,
    loadCollaborativeSessions,
    ensureCollaborativeSession,
    handleApproveObjective,
    handleRejectObjective,
    handleUpdateObjective: handleUpdateObjectiveConfig,
    handleAllocateObjectives
  } = useObjectiveConfigHandlers({
    managerId: currentUserId || '',
    employeeId: memberId,
    employeeName: memberName,
    employeeRole: propMemberRole,
    onRefreshData: onRefreshEvidence,
    year: selectedYear
  })

  // Load employee performance plan (KPAs with objectives and KPIs)
  useEffect(() => {
    const loadMemberPerformancePlan = async () => {
      if (!open || !memberId) return
      
      // Skip loading for legacy years - no platform data exists
      if (isLegacyYear) {
        if (import.meta.env.DEV) console.log(`📊 Legacy year ${selectedYear} detected - skipping performance plan load`)
        setRoleKPIs([])
        return
      }

      setIsLoadingKPIs(true)
      setKpiError(null)
      try {
        // Use employee performance plan endpoint instead of role-based KPIs
        // Pass selectedYear to filter objectives by year
        const response = await performanceService.getEmployeePerformancePlan(memberId, selectedYear)
        
        // Extract KPIs from objectives - filter out rejected AND withdrawn.
        // Withdrawn entries belong to a previous cycle and must not contribute
        // KPIs to the current view (otherwise retired KPIs surface alongside
        // active ones).
        const kpis: RoleKPI[] = []
        const activeObjectives = response.objectives.filter((obj: any) => obj.status !== 'rejected' && obj.status !== 'withdrawn')
        
        activeObjectives.forEach((obj: any) => {
          obj.kpis?.forEach((kpi: any) => {
            kpis.push({
              ...kpi,
              kpa_name: obj.kpa?.display_name || obj.kpa?.name || 'Uncategorized',
              pillar_name: obj.pillar?.name || obj.strategic_objective?.name || 'general',
              strategic_objective_name: obj.strategic_objective?.name || null,
              midyear_progress: kpi.midyear_progress || 0,
              yearend_progress: kpi.yearend_progress || 0,
              individual_objective: {
                id: obj.id,
                title: obj.title,
                description: obj.description,
                status: obj.status,  // Include status for pending_approval check
                progress: obj.progress || 0,
                kpa: obj.kpa,  // Include full KPA data (with id, name, display_name)
                // Include strategic context for cascade visualization
                strategic_objective: obj.strategic_objective,
                strategic_initiative: obj.strategic_initiative,
                operational_initiative: obj.operational_initiative,
                role_type: obj.role_type,
                owner: obj.owner,
                functional_area: obj.functional_area,
                start_date: obj.start_date,
                end_date: obj.end_date
              }
            })
          })
        })
        
        setRoleKPIs(kpis)
      } catch (err) {
        setKpiError(err instanceof Error ? err.message : 'Failed to load performance plan')
        console.error('Failed to load performance plan for', memberId, err)
        // NEVER show fabricated objectives to a People Leader. A failed plan
        // load must surface an honest empty/error state — not mock KPAs.
        // (Previously fell back to getMockRoleKPIs, which silently displayed
        // fake "Teaching Excellence" etc. KPAs whenever the endpoint 500'd,
        // masking the real failure and misleading the reviewer.)
        setRoleKPIs(import.meta.env.DEV ? getMockRoleKPIs(memberRole, memberId) : [])
      } finally {
        setIsLoadingKPIs(false)
      }
    }

    loadMemberPerformancePlan()
  }, [open, memberId, memberRole, refreshTrigger, selectedYear, isLegacyYear])

  // Load KPI snapshots for the selected review period and year
  useEffect(() => {
    if (!open || !memberId) {
      if (import.meta.env.DEV) console.log('📸 Snapshot loading skipped:', { open, memberId })
      return
    }
    
    // Skip snapshot loading for legacy years
    if (isLegacyYear) {
      if (import.meta.env.DEV) console.log(`📸 Legacy year ${selectedYear} - skipping snapshot load`)
      setKpiSnapshots(new Map())
      return
    }

    const loadKPISnapshots = async () => {
      try {
        // Determine review type based on selected period
        const reviewType = selectedReviewPeriod === 'MIDYEAR' ? 'mid-year' : 'year-end'
        if (import.meta.env.DEV) console.log(`📸 Loading snapshots for ${memberId} - ${reviewType} ${selectedYear}`)
        
        // Query user's review sessions to find the completed review for this period/year
        const sessions = await reviewService.getUserReviewSessions(
          memberId,
          reviewType,
          undefined, // Don't filter by completion status yet
          selectedYear
        )
        if (import.meta.env.DEV) console.log(`📸 Found ${sessions.length} sessions:`, sessions)
        
        // Find the completed review session for this period
        // Note: API already filters by review_type and review_year, so just find completed
        const completedSession = sessions.find((s: any) => s.status === 'completed')
        if (import.meta.env.DEV) console.log('📸 Completed session:', completedSession)
        
        if (!completedSession) {
          // No completed review found - clear snapshots
          if (import.meta.env.DEV) console.log(`📸 No completed ${reviewType} review found for ${selectedYear}`)
          setKpiSnapshots(new Map())
          return
        }
        
        // Load full session details with KPA ratings by UUID
        if (import.meta.env.DEV) console.log(`📸 Loading full session: ${completedSession.id}`)
        const session = await reviewService.getReviewSession(completedSession.id)
        if (import.meta.env.DEV) console.log('📸 Session KPA ratings:', session.kpa_ratings)
        
        if (session.kpa_ratings && session.kpa_ratings.length > 0) {
          // Build snapshot map: KPA name -> (KPI ID -> snapshot value)
          const snapshotMap = new Map<string, Map<string, number>>()
          
          session.kpa_ratings.forEach((rating: any) => {
            if (rating.kpi_snapshot && rating.kpi_snapshot.kpis) {
              const kpiMap = new Map<string, number>()
              rating.kpi_snapshot.kpis.forEach((kpi: any) => {
                kpiMap.set(kpi.kpi_id, kpi.current_value)
              })
              // Store ONLY by numeric KPA ID (single source of truth)
              if (rating.kpa_id) {
                snapshotMap.set(rating.kpa_id, kpiMap)
                if (import.meta.env.DEV) console.log(`📸 Loaded ${rating.kpi_snapshot.kpis.length} KPI snapshots for KPA ID: ${rating.kpa_id}`)
              } else {
                console.warn(`⚠️ KPA rating missing kpa_id:`, rating)
              }
            }
          })
          
          setKpiSnapshots(snapshotMap)
          if (import.meta.env.DEV) console.log(`✅ Loaded KPI snapshots for ${reviewType} ${selectedYear}:`, snapshotMap)
        } else {
          if (import.meta.env.DEV) console.log('📸 No KPA ratings or snapshots in session')
          setKpiSnapshots(new Map())
        }
      } catch (error) {
        // Failed to load sessions or session doesn't exist - clear snapshots
        console.error('❌ Failed to load KPI snapshots:', error)
        setKpiSnapshots(new Map())
      }
    }

    loadKPISnapshots()
  }, [open, memberId, selectedReviewPeriod, selectedYear, refreshTrigger])

  // Transform API data into KPA structure
  const realMemberKPAs = useMemo(() => {
    if (!roleKPIs || roleKPIs.length === 0) return []

    // No more hardcoded mappings - we'll use the KPA ID from the API response
    // The performance plan should include kpa_id for each KPI

    // Group KPIs by KPA name
    const kpaMap = new Map<string, typeof roleKPIs>()
    roleKPIs.forEach(kpi => {
      const kpaName = kpi.kpa_name || 'Uncategorized'
      if (!kpaMap.has(kpaName)) {
        kpaMap.set(kpaName, [])
      }
      kpaMap.get(kpaName)!.push(kpi)
    })

    // Get snapshot map for this KPA if available (by numeric KPA ID only)
    const getSnapshotValue = (kpaId: string | null, kpiId: string): number | null => {
      if (!kpaId) return null
      
      const kpaSnapshotMap = kpiSnapshots.get(kpaId)
      if (kpaSnapshotMap) {
        return kpaSnapshotMap.get(kpiId) ?? null
      }
      return null
    }

    // Transform into KPAData array and sort by sort_order
    // KPA order: 1. Operating Financial, 2. Internal Business, 3. Culture, 4. Customer and Stakeholder
    const kpaDataArray = Array.from(kpaMap.entries()).map(([kpaName, kpis]) => {
      // Extract KPA ID and sort_order from the nested objective structure: individual_objective.kpa
      // All KPIs in group should have same KPA, so use first one
      const kpaId = kpis[0]?.individual_objective?.kpa?.id 
        ? String(kpis[0].individual_objective.kpa.id) 
        : null
      const kpaSortOrder = (kpis[0]?.individual_objective?.kpa as any)?.sort_order ?? 999
      
      // Extract unique individual objectives from KPIs and group KPIs by objective
      const objectivesMap = new Map<number, any>()
      kpis.forEach(kpi => {
        if (kpi.individual_objective && kpi.individual_objective.id) {
          const objId = kpi.individual_objective.id
          if (!objectivesMap.has(objId)) {
            // Ensure status is properly preserved (API might use different casing)
            const objStatus = kpi.individual_objective.status || 'active'
            const individualObj = kpi.individual_objective as any
            if (import.meta.env.DEV) console.log(`📋 Objective ${objId} "${individualObj.title}" has status: "${objStatus}", strategic_objective:`, individualObj.strategic_objective)
            objectivesMap.set(objId, {
              id: individualObj.id,
              title: individualObj.title,
              description: individualObj.description,
              role_type: individualObj.role_type,
              owner: individualObj.owner,
              functional_area: individualObj.functional_area,
              status: objStatus,
              progress: individualObj.progress,
              start_date: individualObj.start_date,
              end_date: individualObj.end_date,
              strategic_objective_id: individualObj.strategic_objective_id,
              kpa_id: individualObj.kpa_id,
              pillar_id: individualObj.pillar_id,
              pillar_name: (kpi as any).pillar_name,
              strategic_objective_name: (kpi as any).strategic_objective_name,
              // Include full strategic context (L1→L2→L3) - matching EmployeeJourneyPage pattern
              strategic_objective: individualObj.strategic_objective,
              strategic_initiative: individualObj.strategic_initiative,
              operational_initiative: individualObj.operational_initiative,
              kpa: individualObj.kpa,
              kpis: []  // Initialize KPIs array
            })
          }
          
          // Get period-appropriate KPI value
          // Priority: 1) Review snapshot (if available), 2) Period-specific values, 3) Current value
          const snapshotValue = getSnapshotValue(kpaId, kpi.id)
          let periodValue: number
          let periodProgress: number
          let carriedFromMidYear = false
          // Carry-forward eligible when the objective extends past June
          // (end_date Jul–Dec) or has no end_date (open-ended = full-year).
          const _objEnd = (kpi as any).individual_objective?.end_date
          const carryForwardEligible = !_objEnd || new Date(_objEnd).getMonth() >= 6

          if (snapshotValue !== null) {
            // Use review snapshot if available
            periodValue = snapshotValue
            periodProgress = kpi.target_value > 0 ? (snapshotValue / kpi.target_value) * 100 : 0
          } else {
            // Use period-specific values based on selected period
            if (selectedReviewPeriod === 'MIDYEAR') {
              periodValue = (kpi as any).midyear_value ?? 0
              periodProgress = kpi.midyear_progress || 0
            } else {
              const yearendVal = (kpi as any).yearend_value
              const midyearVal = (kpi as any).midyear_value
              if ((yearendVal === null || yearendVal === undefined) && carryForwardEligible && midyearVal !== null && midyearVal !== undefined) {
                // Year-End with no YE measurement yet → carry the Mid-Year value
                // forward as the baseline for a continuing objective.
                periodValue = midyearVal
                periodProgress = kpi.midyear_progress || 0
                carriedFromMidYear = true
              } else {
                periodValue = yearendVal ?? 0
                periodProgress = kpi.yearend_progress || 0
              }
            }
          }

          // Add this KPI to the objective's KPIs array
          objectivesMap.get(objId).kpis.push({
            id: kpi.id,
            name: kpi.name,
            description: kpi.description,
            current_value: periodValue,
            carriedFromMidYear,
            carryForwardEligible,
            target_value: kpi.target_value,
            unit: kpi.unit,
            status: kpi.status,
            frequency: kpi.frequency,
            trend: kpi.trend || 'stable',
            measurement_history: (kpi as any).measurement_history, // Include measurement history
            // Include measurement configuration for accurate progress calculation
            measurement_direction: (kpi as any).measurement_direction || 'higher_is_better',
            measurement_mode: (kpi as any).measurement_mode || 'numeric',
            rating_scale: (kpi as any).rating_scale,
            rating_options: (kpi as any).rating_options,
            min_value: (kpi as any).min_value,
            max_value: (kpi as any).max_value
          })
        }
      })

      const individualObjectives = Array.from(objectivesMap.values())

      // Calculate progress average based on KPI achievement (matching MemberKPAPanel logic)
      let totalProgress = 0
      let progressCount = 0
      
      individualObjectives.forEach(obj => {
        if (obj.kpis && obj.kpis.length > 0) {
          // Calculate objective progress from its KPIs using enterprise calculation
          const kpiProgressSum = obj.kpis.reduce((sum: number, kpi: any) => {
            // Get measurement configuration from original KPI data (before transformation)
            const originalKpi = kpis.find(k => k.id === kpi.id)
            const mode = ((originalKpi as any)?.measurement_mode || 'numeric') as MeasurementMode
            const direction = ((originalKpi as any)?.measurement_direction || 'higher_is_better') as MeasurementDirection
            
            // Use enterprise progress calculation
            const progressResult = calculateProgress(
              kpi.current_value,
              kpi.target_value,
              direction,
              mode
            )
            return sum + progressResult.progress
          }, 0)
          totalProgress += kpiProgressSum / obj.kpis.length
          progressCount++
        } else if (obj.progress !== undefined) {
          // Fallback to objective.progress if no KPIs
          totalProgress += obj.progress
          progressCount++
        }
      })
      
      const averageProgress = progressCount > 0 ? Math.round(totalProgress / progressCount) : 0

      // Determine risk level from KPI statuses
      const atRiskCount = kpis.filter(k => k.status === 'at-risk' || k.status === 'off-track').length
      const riskLevel = atRiskCount > kpis.length / 2 ? 'high' : atRiskCount > 0 ? 'medium' : 'low'

      // Get pillar from first KPI
      const firstKPI = kpis[0]

      // Generate consistent name-based ID from display name
      const baseKpaName = kpaName.toLowerCase().replace(/\s+/g, '-')
      
      return {
        id: baseKpaName,  // Use consistent name-based ID
        numericId: kpaId,  // Store numeric ID for evidence filtering
        name: baseKpaName,
        display_name: kpaName,
        description: getKpaDescription(kpaName),
        strategic_pillar_id: firstKPI.pillar_name?.toLowerCase().replace(/\s+/g, '-') || 'unknown',
        weight_percentage: 25, // Equal weight distribution
        sort_order: kpaSortOrder, // Include sort_order for proper tab ordering
        riskLevel: riskLevel as 'low' | 'medium' | 'high',
        averageProgress,
        totalEvidence: evidence.length,
        nextReview: memberData?.nextReview?.date || 'Not scheduled',
        individualObjectives,
        underlyingKPIs: kpis.map(kpi => {
          // Get period-appropriate KPI value
          const snapshotValue = getSnapshotValue(kpaId, kpi.id)
          let periodValue: number
          let periodProgress: number
          let carriedFromMidYear = false
          const _objEnd = (kpi as any).individual_objective?.end_date
          const carryForwardEligible = !_objEnd || new Date(_objEnd).getMonth() >= 6

          if (snapshotValue !== null) {
            periodValue = snapshotValue
            periodProgress = kpi.target_value > 0 ? (snapshotValue / kpi.target_value) * 100 : 0
            if (import.meta.env.DEV) console.log(`📸 Using snapshot for KPI "${kpi.name}": ${kpi.current_value} → ${snapshotValue}`)
          } else {
            // Use period-specific values based on selected period
            if (selectedReviewPeriod === 'MIDYEAR') {
              periodValue = (kpi as any).midyear_value ?? 0
              periodProgress = kpi.midyear_progress || 0
            } else {
              const yearendVal = (kpi as any).yearend_value
              const midyearVal = (kpi as any).midyear_value
              if ((yearendVal === null || yearendVal === undefined) && carryForwardEligible && midyearVal !== null && midyearVal !== undefined) {
                periodValue = midyearVal
                periodProgress = kpi.midyear_progress || 0
                carriedFromMidYear = true
              } else {
                periodValue = yearendVal ?? 0
                periodProgress = kpi.yearend_progress || 0
              }
            }
          }

          return {
            id: kpi.id,
            name: kpi.name,
            description: kpi.description,
            target_value: kpi.target_value,
            current_value: periodValue,
            carriedFromMidYear,
            carryForwardEligible,
            unit: kpi.unit,
            status: kpi.status,
            frequency: kpi.frequency,
            owner: kpi.owner || memberName,
            functional_area: kpi.functional_area,
            milestones: kpi.milestones.map(m => ({
              id: m.id,
              title: m.title,
              description: m.description || '',
              due_date: m.due_date,
              status: m.status,
              progress: m.progress,
              milestone_type: m.milestone_type
            })),
            progress: periodProgress,
            measurements: [],
            trend: 'stable' as const,
            measurement_history: (kpi as any).measurement_history // Include measurement history
          }
        })
      }
    })
    
    // Sort KPAs by sort_order to ensure consistent ordering across the platform
    // Order: 1. Operating Financial, 2. Internal Business, 3. Culture, 4. Customer and Stakeholder
    return kpaDataArray.sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999))
  }, [roleKPIs, evidence, memberName, kpiSnapshots, memberData, selectedReviewPeriod, selectedYear, currentYear])

  // Always use realMemberKPAs since it has period-specific value logic applied
  // The internal data transforms KPIs based on selectedReviewPeriod
  // Sort memberKPAs fallback by sort_order as well to ensure consistent ordering
  const displayKPAs = realMemberKPAs.length > 0 
    ? realMemberKPAs 
    : [...memberKPAs].sort((a, b) => ((a as any).sort_order || 999) - ((b as any).sort_order || 999))
  
  // Debug: Log which data source is being used and sample measurement data
  const sampleMeasurement = (displayKPAs[0]?.underlyingKPIs?.[0] as any)?.measurement_history?.[0]
  if (import.meta.env.DEV) console.log(`📋 Dialog using: ${realMemberKPAs.length > 0 ? 'realMemberKPAs INTERNAL' : 'memberKPAs PROP'} (period: ${selectedReviewPeriod}, internal length: ${realMemberKPAs.length})`)
  if (sampleMeasurement) {
    if (import.meta.env.DEV) console.log(`📋 Sample measurement from displayKPAs:`, sampleMeasurement)
  }
  
  // Filter evidence by selected review period (handle undefined/null review periods)
  const filteredEvidence = useMemo(() => {
    // Match the selected period OR include entries opted into both periods
    // via the dual-period flag. Legacy items with no period still need to
    // be assigned a period before they're surfaced here.
    return evidence.filter(e =>
      e.reviewPeriod === selectedReviewPeriod ||
      e.appliesToBothPeriods === true
    )
  }, [evidence, selectedReviewPeriod])

  // Top-level Impact Portfolio tab — what the team member RELEASED for the
  // formal review of the selected period (Mid-Year / Year-End), PLUS every
  // measurement for the period (KPI backing data is always shared, including
  // measurements the People Leader recorded). Release is what's gated, NOT
  // capture period: an entry released only for a Q1/Q3 check-in is excluded
  // here (it lives in that check-in's workspace).
  const releasedPortfolioEvidence = useMemo(
    () => evidence.filter(e => visibleForFormalPeriod(e, selectedReviewPeriod)),
    [evidence, selectedReviewPeriod],
  )

  // Year-End carried-forward measurements: for carry-eligible KPIs (objective
  // extends past June) that have NO Year-End measurement yet, surface the
  // Mid-Year measurement as read-only "carried forward" context so the YE
  // Impact Portfolio isn't empty while the KPI value carries.
  const carriedMidYearEvidence = useMemo(
    () => buildCarriedMidYearEvidence(evidence, displayKPAs, selectedReviewPeriod),
    [evidence, displayKPAs, selectedReviewPeriod],
  )
  // With each KPA's numeric id, which saved entries carry, so a KPA tag and the
  // area filter find the entries linked to it (HR UAT list #2).
  const portfolioKpaOptions = useMemo(
    () => displayKPAs.map(k => ({ id: k.id, numericId: k.numericId, name: k.display_name || k.name })),
    [displayKPAs],
  )
  // Cross-KPA KPI list (period-aware) for the top-level portfolio's Record
  // Measurement flow — each KPI is tagged with its kpaId so picking a KPA in the
  // dialog narrows the KPI selector. Without this the PL could pick a KPA but no
  // KPI. Built from every KPA's underlying KPIs + objective KPIs, de-duped.
  const portfolioAvailableKPIs = useMemo(() => {
    const out: any[] = []
    displayKPAs.forEach((kpa: any) => {
      const add = (kpi: any) => {
        if (!kpi || out.some(k => k.id === kpi.id)) return
        const periodValue = selectedReviewPeriod === 'MIDYEAR'
          ? (kpi.midyear_value ?? kpi.current_value)
          : (kpi.yearend_value ?? kpi.current_value)
        out.push({ ...kpi, kpaId: kpa.id, current_value: periodValue ?? undefined })
      }
      ;(kpa.underlyingKPIs || []).forEach(add)
      ;(kpa.individualObjectives || []).forEach((obj: any) => (obj.kpis || []).forEach(add))
    })
    return out
  }, [displayKPAs, selectedReviewPeriod])

  // Calculate tenure from hire date
  const calculateTenure = (hireDate?: string): number => {
    if (!hireDate) return 0
    const hire = new Date(hireDate)
    const now = new Date()
    return Math.floor((now.getTime() - hire.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
  }

  // Map worker_type to display employment type
  const getEmploymentType = (workerType?: string): 'Permanent' | 'FTC' | 'IC' => {
    if (!workerType) return 'Permanent'
    const type = workerType.toLowerCase()
    if (type.includes('contract') || type.includes('ftc') || type.includes('fixed')) return 'FTC'
    if (type.includes('independent') || type.includes('contractor') || type === 'ic') return 'IC'
    return 'Permanent'
  }

  // Map API performance rating to MemberDetails format - returns null if no rating
  const normalizePerformanceRating = (rating?: string | null): 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr' | null => {
    if (!rating) return null
    const normalized = rating.toLowerCase().replace(/_/g, '-')
    if (normalized === 'exceptional') return 'exceptional'
    if (normalized === 'full' || normalized === 'proficient') return 'full'
    if (normalized === 'not-yet-full' || normalized === 'developing' || normalized === 'not_yet_full') return 'not-yet-full'
    if (normalized === 'weak' || normalized === 'inadequate') return 'weak'
    return null
  }

  // Map API trend to MemberDetails format - returns null if no trend data
  const normalizeTrend = (trend?: string | null): 'improving' | 'maintaining' | 'declining' | null => {
    if (!trend) return null
    const normalized = trend.toLowerCase()
    if (normalized === 'improving' || normalized === 'up') return 'improving'
    if (normalized === 'declining' || normalized === 'down') return 'declining'
    if (normalized === 'maintaining' || normalized === 'stable') return 'maintaining'
    return null
  }

  // Build memberDetails from API data or fallback to defaults
  const memberDetails: MemberDetails = useMemo(() => {
    // Use F&O data if available
    const foData = memberData?.foData
    const apiPerformance = memberData?.performance
    const apiAlerts = memberData?.alerts || []

    // Calculate next review from review sessions (prefer this over memberData.nextReview)
    const calculateNextReview = (): { type: string; date: string; status: 'scheduled' | 'pending-scheduling' | 'overdue' } => {
      // Get scheduled/pending reviews for this member, sorted by date
      const upcomingReviews = memberReviewSessions
        .filter(s => 
          s.user_id === memberId && 
          (s.status === 'scheduled' || s.status === 'pending') &&
          s.review_date
        )
        .sort((a, b) => new Date(a.review_date!).getTime() - new Date(b.review_date!).getTime())
      
      const nextSession = upcomingReviews[0]
      
      if (nextSession && nextSession.review_date) {
        const reviewDate = new Date(nextSession.review_date)
        const now = new Date()
        const isOverdue = reviewDate < now
        
        // Format review type for display
        const typeMap: Record<string, string> = {
          'midyear': 'Mid-Year 1:1',
          'yearend': 'Year-End 1:1',
          'q1_checkin': 'Q1 Check-in',
          'q3_checkin': 'Q3 Check-in'
        }
        
        return {
          type: typeMap[nextSession.review_type] || nextSession.review_type,
          date: reviewDate.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }),
          status: isOverdue ? 'overdue' : 'scheduled'
        }
      }
      
      // Fall back to memberData.nextReview if no sessions found
      const apiNextReview = memberData?.nextReview
      if (apiNextReview && apiNextReview.date) {
        return {
          type: apiNextReview.type || 'Check-in',
          date: apiNextReview.date,
          status: apiNextReview.status === 'overdue' ? 'overdue' : 'scheduled'
        }
      }
      
      return {
        type: 'Check-in',
        date: 'Not scheduled',
        status: 'pending-scheduling'
      }
    }

    return {
      id: memberId,
      name: memberName,
      role: foData?.job_title || memberData?.job_title || propMemberRole || 'Team Member',
      campus: foData?.campus || memberData?.campus || 'Not specified',
      department: foData?.functional_area || foData?.department || memberData?.department || 'Not specified',
      employmentType: getEmploymentType(foData?.worker_type),
      tenureYears: calculateTenure(foData?.original_hire_date),
      performance: {
        current: normalizePerformanceRating(apiPerformance?.current),
        trend: normalizeTrend(apiPerformance?.trend),
        lastRating: normalizePerformanceRating(apiPerformance?.lastRating)
      },
      // Use displayKPAs for KPA summary - calculated dynamically
      kpas: displayKPAs.map(kpa => ({
        id: kpa.id,
        name: kpa.display_name || kpa.name,
        progress: kpa.averageProgress || 0,
        objectivesCount: kpa.individualObjectives?.filter((obj: any) => obj.status !== 'rejected').length || 0,
        kpisCount: kpa.underlyingKPIs?.length || 0,
        evidenceCount: kpa.totalEvidence || 0,
        status: kpa.riskLevel === 'high' ? 'off-track' as const : 
                kpa.riskLevel === 'medium' ? 'at-risk' as const : 
                'on-track' as const
      })),
      nextReview: calculateNextReview(),
      alerts: apiAlerts.length > 0 ? apiAlerts : 
        displayKPAs.filter(kpa => kpa.riskLevel === 'high' || kpa.riskLevel === 'medium')
          .map(kpa => `${kpa.display_name || kpa.name} is ${kpa.riskLevel === 'high' ? 'off-track' : 'at-risk'}`)
    }
  }, [memberId, memberName, memberData, propMemberRole, displayKPAs, memberReviewSessions])

  const getPerformanceColor = (performance: string | null) => {
    switch (performance) {
      case 'exceptional': return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'full': return 'bg-green-100 text-green-800 border-green-200'
      case 'not-yet-full': return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'weak': return 'bg-red-100 text-red-800 border-red-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getTrendIcon = (trend: string | null) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="h-4 w-4 text-green-600" />
      case 'maintaining': return <Minus className="h-4 w-4 text-blue-600" />
      case 'declining': return <TrendingDown className="h-4 w-4 text-red-600" />
      default: return null
    }
  }

  if (!open) return null

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[1536px] h-[90vh] flex flex-col overflow-hidden">
        {/* Close button - always visible */}
        <Button variant="ghost" className="absolute top-2 right-2 md:top-4 md:right-4 z-30 text-white bg-black/20 hover:bg-black/40 rounded-full h-8 w-8 p-0" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
        {/* In-context guidance video for the team-member deep dive (auto-hides
            until an admin publishes one). Sits just left of the close control on
            the navy masthead. */}
        <div className="absolute top-2 right-12 md:top-4 md:right-16 z-30">
          <VideoHelpButton
            surface="member-deep-dive"
            iconOnly
            className="border-white/30 bg-white/15 text-white hover:bg-white/25"
          />
        </div>

        {/* Scrollable content area (header scrolls away) */}
        <div className="flex-1 overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-br from-heritage-blue to-future-blue text-white p-4 md:p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 md:gap-4 mb-3">
                <div className="h-12 w-12 md:h-16 md:w-16 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg md:text-2xl font-bold">
                    {memberDetails.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl md:text-2xl font-bold truncate">{memberDetails.name}</h2>
                  <p className="text-blue-100 text-sm md:text-base truncate">{memberDetails.role} • {memberDetails.department}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {memberDetails.campus && memberDetails.campus !== 'Not specified' && (
                      <Badge className="bg-white/20 text-white border-white/30">
                        {memberDetails.campus}
                      </Badge>
                    )}
                    {memberDetails.tenureYears > 0 && (
                      <Badge className="bg-white/20 text-white border-white/30">
                        {memberDetails.tenureYears} {memberDetails.tenureYears === 1 ? 'year' : 'years'}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Stats & Live Indicators */}
              <div className="flex flex-wrap items-center gap-3 md:gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  <span>Impact & Growth: </span>
                  {memberDetails.performance.current ? (
                    <Badge className={`${getPerformanceColor(memberDetails.performance.current)} border`}>
                      {memberDetails.performance.current.replace('-', ' ')}
                    </Badge>
                  ) : (
                    <span className="text-blue-200 italic">Not yet rated</span>
                  )}
                </div>
                {memberDetails.performance.trend && (
                  <div className="flex items-center gap-2">
                    {getTrendIcon(memberDetails.performance.trend)}
                    <span className="capitalize">{memberDetails.performance.trend}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>
                    Next Check-in: {memberDetails.nextReview.date === 'Not scheduled' ? (
                      <span className="text-amber-200">Not scheduled</span>
                    ) : memberDetails.nextReview.status === 'overdue' ? (
                      <span className="text-red-300">{memberDetails.nextReview.date} (Overdue)</span>
                    ) : (
                      memberDetails.nextReview.date
                    )}
                  </span>
                </div>
              </div>
              
              {/* Year and Review Period Selectors */}
              <div className="mt-4 flex flex-wrap items-center gap-3 md:gap-4">
                {/* Year Selector */}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-100" />
                  <span className="text-sm text-blue-100 font-medium">Year:</span>
                  <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                    <SelectTrigger className="w-[85px] bg-white/10 border-white/30 text-white hover:bg-white/20">
                      <span>{selectedYear}</span>
                    </SelectTrigger>
                    <SelectContent>
                      {availableYears.map(year => (
                        <SelectItem key={year} value={year.toString()}>
                          <div className="flex items-center gap-2">
                            {year}
                            {year < SYSTEM_LAUNCH_YEAR && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Legacy</span>}
                            {year === currentYear && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Current</span>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Review Period Selector */}
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-blue-100" />
                  <span className="text-sm text-blue-100 font-medium">Period:</span>
                  <Select value={selectedReviewPeriod} onValueChange={(value) => setSelectedReviewPeriod(value as 'MIDYEAR' | 'YEAREND')}>
                    <SelectTrigger className="w-[145px] bg-white/10 border-white/30 text-white hover:bg-white/20">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${selectedReviewPeriod === 'MIDYEAR' ? 'bg-blue-400' : 'bg-green-400'}`} />
                        <span>{selectedReviewPeriod === 'MIDYEAR' ? 'Mid-Year (Jan-Jun)' : 'Year-End (Jul-Dec)'}</span>
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MIDYEAR">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full" />
                          <span>Mid-Year (Jan-Jun)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="YEAREND">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full" />
                          <span>Year-End (Jul-Dec)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Filter Stats Badge */}
                <Badge className="bg-white/20 text-white border-white/30">
                  {filteredEvidence.length} of {evidence.length} items
                </Badge>

                {/* Simple/Detailed view toggle (global, synced across surfaces) */}
                <ViewModeToggle className="ml-auto rounded-full bg-white/10 border border-white/30 px-2.5 py-1 [&_span]:text-blue-50" />

                {/* Historical View Indicator */}
                {isHistoricalView && (
                  <Badge className="bg-yellow-500/30 text-yellow-100 border-yellow-400/40 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {selectedYear < currentYear 
                      ? `${selectedYear} - Historical (Read Only)` 
                      : `${selectedReviewPeriod === 'MIDYEAR' ? 'Mid-Year' : 'Year-End'} Check-in Completed (Read Only)`}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

          <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col" data-demo="member-dialog">
            {/* Mobile Dropdown Tab Selector */}
            <div className="md:hidden sticky top-0 z-20 bg-white border-b p-3 shadow-md">
              <Select value={activeTab} onValueChange={handleTabChange}>
                <SelectTrigger className="w-full bg-gray-50 border-gray-200">
                  <SelectValue placeholder="Select tab" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="overview">
                    <div className="flex items-center gap-2">
                      <BarChart4 className="h-4 w-4" />
                      Overview
                    </div>
                  </SelectItem>
                  {!isLegacyYear && displayKPAs.map((kpa) => (
                    <SelectItem key={kpa.id} value={`kpa-${kpa.id}`}>
                      <div className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4" />
                        <span className="truncate">{kpa.display_name || kpa.name}</span>
                        <span className="text-xs text-gray-500 ml-1">{kpa.averageProgress}%</span>
                      </div>
                    </SelectItem>
                  ))}
                  {!isLegacyYear && (
                    <SelectItem value="impact-portfolio">
                      <div className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4" />
                        Impact Portfolio
                      </div>
                    </SelectItem>
                  )}
                  {!isLegacyYear && canRecordObservations && (
                    <SelectItem value="leader-observations">
                      <div className="flex items-center gap-2">
                        <NotebookPen className="h-4 w-4" />
                        Leader Observations
                      </div>
                    </SelectItem>
                  )}
                  {!isLegacyYear && (
                    <SelectItem value="feedback">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        Feedback
                      </div>
                    </SelectItem>
                  )}
                  {!isLegacyYear && (
                    <SelectItem value="development">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        Growth Plan
                      </div>
                    </SelectItem>
                  )}
                  {!isLegacyYear && (
                    <SelectItem value="strategic">
                      <div className="flex items-center gap-2">
                        <Layers className="h-4 w-4" />
                        Strategic Context
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Desktop Tab Bar */}
            <TabsList ref={desktopTabBarRef} className="sticky top-0 z-20 hidden md:flex flex-wrap rounded-none border-b bg-white h-auto p-0 shadow-md">
              {/* Overview Tab */}
              <TabsTrigger value="overview" className="gap-1.5 h-14 px-3 py-2 data-[state=active]:bg-heritage-blue data-[state=active]:text-white flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <BarChart4 className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium whitespace-nowrap">Overview</span>
                </div>
              </TabsTrigger>
              
              {/* Dynamic KPA Tabs - Hidden for legacy years */}
              {!isLegacyYear && displayKPAs.map((kpa, kpaIndex) => (
                  <TabsTrigger
                    key={kpa.id}
                    value={`kpa-${kpa.id}`}
                    className="gap-2 h-14 px-3 py-2 bg-blue-50/60 border-x border-blue-100 data-[state=active]:bg-heritage-blue data-[state=active]:text-white data-[state=active]:border-heritage-blue min-w-[140px] max-w-[200px] 2xl:max-w-none flex-grow"
                    title={kpa.display_name || kpa.name}
                    {...(kpaIndex === 0 ? { 'data-demo': 'member-kpa-tab' } : {})}
                  >
                    <div className="flex items-center gap-1.5 min-w-0 2xl:min-w-fit">
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <BookOpen className="h-4 w-4" />
                      </div>
                      <span className="text-sm font-medium truncate 2xl:overflow-visible 2xl:whitespace-nowrap">
                        {kpa.display_name || kpa.name}
                      </span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-xs opacity-75">{kpa.averageProgress}%</span>
                      </div>
                    </div>
                  </TabsTrigger>
              ))}
              
              {/* Impact Portfolio Tab — the team member's released portfolio
                  (KPA + General), gated to what they shared for this period.
                  Hidden for legacy years. */}
              {!isLegacyYear && (
              <TabsTrigger value="impact-portfolio" className="gap-1.5 h-14 px-3 py-2 data-[state=active]:bg-heritage-blue data-[state=active]:text-white flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <FolderOpen className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium whitespace-nowrap">Impact Portfolio</span>
                </div>
              </TabsTrigger>
              )}

              {/* Leader Observations Tab — the People Leader's private notes about
                  this team member, beside the Impact Portfolio where they find
                  them: a general observation at the bottom of the portfolio went
                  unnoticed (HR UAT list #2). */}
              {!isLegacyYear && canRecordObservations && (
              <TabsTrigger value="leader-observations" className="gap-1.5 h-14 px-3 py-2 data-[state=active]:bg-heritage-blue data-[state=active]:text-white flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <NotebookPen className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium whitespace-nowrap">Leader Observations</span>
                </div>
              </TabsTrigger>
              )}

              {/* Feedback Tab - Hidden for legacy years */}
              {!isLegacyYear && (
              <TabsTrigger value="feedback" className="gap-1.5 h-14 px-3 py-2 data-[state=active]:bg-heritage-blue data-[state=active]:text-white flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <MessageSquare className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium whitespace-nowrap">Feedback</span>
                </div>
              </TabsTrigger>
              )}
              
              {/* Development Tab - Hidden for legacy years */}
              {!isLegacyYear && (
              <TabsTrigger value="development" className="gap-1.5 h-14 px-3 py-2 data-[state=active]:bg-heritage-blue data-[state=active]:text-white flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <Target className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium whitespace-nowrap">Growth Plan</span>
                </div>
              </TabsTrigger>
              )}
              
              {/* Strategic Context Tab - Hidden for legacy years */}
              {!isLegacyYear && (
              <TabsTrigger value="strategic" className="gap-1.5 h-14 px-3 py-2 data-[state=active]:bg-heritage-blue data-[state=active]:text-white flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <Layers className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium whitespace-nowrap">Strategic Context</span>
                </div>
              </TabsTrigger>
              )}
            </TabsList>

            {/* Overview Tab - Using PerformanceOnePager for consistent styling */}
            <TabsContent value="overview" className="px-4 py-4 md:px-8 md:py-6 m-0">
              {/* Legacy Year View - Show F&O placeholder instead of current data */}
              {isLegacyYear ? (
                <div className="space-y-6">
                  {/* Legacy Year Banner */}
                  <Card className="border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50 to-orange-50">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Award className="h-8 w-8 text-amber-600 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-amber-900 mb-2">Viewing Legacy Impact & Growth Data ({selectedYear})</h3>
                          <p className="text-sm text-amber-800 mb-3">
                            This impact & growth data is from the Finance & Operations (F&O) system and is read-only.
                          </p>
                          <div className="bg-amber-100/50 rounded-lg p-4 mb-4">
                            <h4 className="font-medium text-amber-900 mb-2">What you can view:</h4>
                            <ul className="text-sm text-amber-800 space-y-1">
                              <li>• Historical impact & growth indicators from check-in cycles</li>
                              <li>• Previous year summaries and outcomes</li>
                              <li>• Archived calibration data from F&O</li>
                            </ul>
                          </div>
                          <div className="text-xs text-amber-700">
                            <strong>Note:</strong> To view and manage current objectives, select {SYSTEM_LAUNCH_YEAR} or later.
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* F&O Historical Data Placeholder */}
                  <Card className="border-dashed border-2 border-gray-200">
                    <CardContent className="p-12 text-center">
                      <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <Calendar className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-700 mb-2">F&O Historical Data</h3>
                      <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
                        Historical impact & growth data from {selectedYear} will be displayed here once F&O integration is complete.
                      </p>
                      <Button 
                        variant="outline" 
                        onClick={() => setSelectedYear(currentYear)}
                        className="gap-2"
                      >
                        <Calendar className="h-4 w-4" />
                        Return to Current Year ({currentYear})
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <>
              {/* Performance Summary Card - Manager Context */}
              <Card className="mb-6">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2">
                    <BarChart4 className="h-5 w-5 text-heritage-blue" />
                    Impact & Growth Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-6">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Current Impact & Growth Indicator</p>
                      {memberDetails.performance.current ? (
                        <Badge className={`${getPerformanceColor(memberDetails.performance.current)} border text-base`}>
                          {memberDetails.performance.current.replace('-', ' ')}
                        </Badge>
                      ) : (
                        <span className="text-gray-500 italic">Not yet rated</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Trend</p>
                      {memberDetails.performance.trend ? (
                        <div className="flex items-center gap-2">
                          {getTrendIcon(memberDetails.performance.trend)}
                          <span className="font-semibold capitalize">{memberDetails.performance.trend}</span>
                        </div>
                      ) : (
                        <span className="text-gray-500 italic">No trend data</span>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Last Impact & Growth Indicator</p>
                      {memberDetails.performance.lastRating ? (
                        <Badge className={`${getPerformanceColor(memberDetails.performance.lastRating)} border`}>
                          {memberDetails.performance.lastRating.replace('-', ' ')}
                        </Badge>
                      ) : (
                        <span className="text-gray-500 italic">No previous indicator</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* PerformanceOnePager - Same styling as EmployeeKPAView */}
              <PerformanceOnePager
                employeeName={memberName}
                kpas={displayKPAs}
                lastReviewDate={undefined}
                nextReviewDate={memberDetails.nextReview.date !== 'Not scheduled' ? memberDetails.nextReview.date : undefined}
                developmentPlan={{
                  goals: [],
                  completedGoals: 0,
                  totalGoals: 0
                }}
                alerts={memberDetails.alerts}
                onNavigateToKPA={(kpaId) => handleTabChange(`kpa-${kpaId}`)}
                onNavigateToObjective={(objectiveId) => {
                  // Find the KPA that contains this objective and navigate to it
                  const kpa = displayKPAs.find(k => k.individualObjectives.some(obj => obj.id === objectiveId))
                  if (kpa) {
                    handleTabChange(`kpa-${kpa.id}`)
                  }
                }}
                userRole="manager"
                journeyViewMode={journeyViewMode}
              />
                </>
              )}
            </TabsContent>

            {/* Dynamic KPA Tabs Content - Only render for non-legacy years */}
            {!isLegacyYear && displayKPAs.map((kpa) => {
              // Filter data for this specific KPA - check both string ID and numeric ID
              const kpaEvidence = [...filteredEvidence, ...carriedMidYearEvidence].filter(e => {
                const matchStringId = e.kpaId === kpa.id
                const matchObjectiveId = e.objectiveId === kpa.id
                // API returns kpaId as a stringified number ("3"); numericId is
                // a number (3). Strict === never matched — coerce both sides.
                const matchNumericId = kpa.numericId != null && e.kpaId != null &&
                  String(e.kpaId) === String(kpa.numericId)
                // Match KPI-linked entries (measurements) by their KPI → this KPA,
                // even when the row carries no kpa_id (e.g. a measurement recorded
                // with only a KPI). Mirrors ConductReviewWorkspace.
                const matchKpi = !!e.kpiId && (
                  (kpa as any).underlyingKPIs?.some((k: any) => k.id === e.kpiId) ||
                  (kpa as any).individualObjectives?.some((obj: any) => obj.kpis?.some((k: any) => k.id === e.kpiId))
                )
                // General (no-KPA) entries no longer surface under every KPA —
                // they live in the dedicated top-level Impact Portfolio tab.
                return matchStringId || matchObjectiveId || matchNumericId || matchKpi
              })
              
              // Create a stable key based on KPA ID and latest measurement timestamp
              const latestMeasurement = kpa.underlyingKPIs
                ?.flatMap(kpi => (kpi as any).measurement_history || [])
                .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
              const measurementKey = latestMeasurement?.timestamp || 'no-measurements'
              
              // Debug: Log when measurement key changes
              if (import.meta.env.DEV) console.log(`🔑 KPA ${kpa.id} measurement key: ${measurementKey}`)
              
              return (
                <TabsContent key={kpa.id} value={`kpa-${kpa.id}`} className="m-0">
                  {onAddEvidence && onDeleteEvidence && onUploadDocument ? (
                    <MemberKPAPanel
                      key={`${kpa.id}-${measurementKey}`}
                      kpa={kpa}
                      memberId={memberId}
                      memberName={memberName}
                      currentUserId={currentUserId}
                      reviewPeriod={selectedReviewPeriod}
                      stickyTopOffset={tabBarHeight}
                      evidence={kpaEvidence}
                      checkEvidenceEditable={canEditEvidence}
                      onAddEvidence={handleAddEvidenceWithPeriod}
                      onUpdateEvidence={handleUpdateEvidence}
                      onUpdateEvidenceClassification={onUpdateEvidenceClassification}
                      onRefreshEvidence={onRefreshEvidence}
                      onDeleteEvidence={handleDeleteEvidence}
                      onUploadDocument={onUploadDocument}
                      onCreateObjective={(obj) => onCreateObjective?.({ ...obj, kpaId: kpa.id })}
                      onUpdateObjective={(id, updates) => onUpdateObjective?.(id, updates)}
                      onDeleteObjective={(id) => onDeleteObjective?.(id)}
                      onViewObjectiveDetails={(obj) => onViewObjectiveDetails?.(obj)}
                      onApproveObjective={onApproveObjective}
                      onRejectObjective={onRejectObjective}
                      onViewKPIDetails={(kpi) => console.log('View KPI:', kpi)}
                      onOpenObjectiveConfig={handleOpenObjectiveConfig}
                      highlightedKPIId={highlightedKPIId}
                      journeyViewMode={journeyViewMode}
                    />
                  ) : (
                    <div className="p-6">
                      <Card>
                        <CardContent className="p-12 text-center">
                          <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-600">KPA Panel requires handler functions</p>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>
              )
            })}
            
            {/* Impact Portfolio Tab Content — the FULL released set (KPA +
                General) in one place, mirroring the team member's own "my
                Impact Portfolio". kpaId undefined → mixed view with KPA/General
                chips + the shared filter bar. Read-only for the PL. */}
            {!isLegacyYear && (
              <TabsContent value="impact-portfolio" className="px-4 py-4 md:px-8 md:py-6 m-0">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Impact Portfolio</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Everything {memberName?.split(' ')[0] || 'this team member'} shared for{' '}
                    {selectedReviewPeriod === 'MIDYEAR' ? 'Mid-Year' : 'Year-End'} — across all areas, plus general entries.
                  </p>
                </div>
                <EvidencePortfolio
                  evidence={[...releasedPortfolioEvidence, ...carriedMidYearEvidence]}
                  kpaId={undefined}
                  kpaOptions={portfolioKpaOptions}
                  availableKPIs={portfolioAvailableKPIs}
                  userRole="manager"
                  currentUserId={currentUserId}
                  targetUserId={memberId}
                  reviewPeriod={selectedReviewPeriod}
                  skipPeriodDisplayFilter={true}
                  viewMode={journeyViewMode === 'detailed' ? 'detailed' : 'simple'}
                  showManagerCannotAddNotice={true}
                  ownerDisplayName={memberName}
                  checkEvidenceEditable={canEditEvidence}
                  onAddEvidence={handleAddEvidenceWithPeriod}
                  onUpdateEvidence={handleUpdateEvidence}
                  onRefreshEvidence={onRefreshEvidence}
                  onDeleteEvidence={handleDeleteEvidence}
                  onUploadDocument={onUploadDocument || (async () => {})}
                  onClassifyEvidence={onUpdateEvidenceClassification}
                />
              </TabsContent>
            )}

            {/* Leader Observations Tab Content — the People Leader's private notes
                about this team member (HR UAT list #2): every observation they
                recorded for the period, each tagged with its KPA or General, with
                the area filter; adding one links a KPA optionally. Includes
                feedback filed into their observations. The team member never
                sees these. */}
            {!isLegacyYear && canRecordObservations && (
              <TabsContent value="leader-observations" className="px-4 py-4 md:px-8 md:py-6 m-0">
                <LeaderObservationsPanel
                  memberName={memberName}
                  evidence={evidence}
                  kpaOptions={portfolioKpaOptions}
                  currentUserId={currentUserId}
                  targetUserId={memberId}
                  reviewPeriod={selectedReviewPeriod}
                  readOnly={isHistoricalView}
                  viewMode={journeyViewMode === 'detailed' ? 'detailed' : 'simple'}
                  checkEvidenceEditable={canEditEvidence}
                  onAddEvidence={handleAddEvidenceWithPeriod}
                  onUpdateEvidence={handleUpdateEvidence}
                  onRefreshEvidence={onRefreshEvidence}
                  onDeleteEvidence={handleDeleteEvidence}
                  onUploadDocument={onUploadDocument || (async () => {})}
                />
              </TabsContent>
            )}

            {/* Legacy KPAs Summary (keeping for fallback) - Only for non-legacy years */}
            {!isLegacyYear && (
            <TabsContent value="kpas-legacy" className="p-3 md:p-6 m-0 space-y-4">
              {displayKPAs.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No KPAs assigned to this team member yet.</p>
                  </CardContent>
                </Card>
              ) : (
                displayKPAs.map((kpa) => (
                  <Card key={kpa.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => onViewKPAWorkspace?.(kpa)}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <BookOpen className="h-5 w-5 text-heritage-blue" />
                            <h3 className="text-lg font-semibold">{kpa.name}</h3>
                          </div>
                          <p className="text-sm text-gray-600 mb-4">{kpa.description}</p>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4">
                            <div>
                              <p className="text-sm text-gray-600">Progress</p>
                              <p className="text-2xl font-bold text-heritage-blue">{kpa.averageProgress}%</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Objectives</p>
                              <p className="text-2xl font-bold text-green-600">{kpa.individualObjectives.length}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">KPIs</p>
                              <p className="text-2xl font-bold text-blue-600">{kpa.underlyingKPIs.length}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600">Impact Items</p>
                              <p className="text-2xl font-bold text-purple-600">{kpa.totalEvidence}</p>
                            </div>
                          </div>

                          <Progress value={kpa.averageProgress} className="h-3 mb-3" />
                          
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Next check-in: {kpa.nextReview}</span>
                            <Button variant="outline" size="sm" onClick={(e) => {
                              e.stopPropagation()
                              onViewKPAWorkspace?.(kpa)
                            }}>
                              Open Workspace →
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
            )}
            
            {/* Feedback Tab - Stakeholder Feedback System - Only for non-legacy years */}
            {!isLegacyYear && (
            <TabsContent value="feedback" className="p-3 md:p-6 m-0">
              <StakeholderFeedbackSystem
                userRole="manager"
                requests={feedbackRequests || []}
                userId={memberId}
                onCreateRequest={onCreateFeedbackRequest || (() => console.log('Create feedback request'))}
                onRefresh={onRefreshFeedback}
              />
            </TabsContent>
            )}
            
            {/* Growth Plan Tab - the team member's Growth Plan & Career Planning - Only for non-legacy years */}
            {!isLegacyYear && (
            <TabsContent value="development" className="p-3 md:p-6 m-0">
              <EmployeeIDPView
                userId={memberId}
                embedded={true}
                userRole="manager"
                hideHeader={true}
              />
            </TabsContent>
            )}
            
            {/* Strategic Context Tab - Cascade Visualization - Only for non-legacy years */}
            {!isLegacyYear && (
            <TabsContent value="strategic" className="p-3 md:p-6 m-0">
              <StrategicCascadeViewer
                cascadeData={displayKPAs.flatMap(kpa => 
                  // Group by individual objective, not by KPI - filter out rejected objectives
                  kpa.individualObjectives
                    .filter(obj => obj.status !== 'rejected')
                    .map(obj => ({
                      // L1: Strategic Objective (SCALE, QUALITY, CULTURE, IMPACT)
                      // Use strategic_objective.name, fallback to pillar_name or strategic_objective_name from KPI level
                      pillar_name: obj.strategic_objective?.name || obj.strategic_objective?.display_name || (obj as any).pillar_name || (obj as any).strategic_objective_name || 'Uncategorized',
                      // L2: Strategic Initiative (if available, otherwise use a meaningful default)
                      strategic_objective_name: obj.strategic_initiative?.name || 
                        (obj.strategic_objective?.display_name ? `${obj.strategic_objective.display_name} - ${obj.strategic_initiative?.name || 'General'}` : 
                        (obj as any).strategic_objective_name || 'Not Aligned'),
                      // KPA (functional area)
                      kpa_name: kpa.display_name || kpa.name,
                      // Store full strategic context for detailed view
                      strategic_context: {
                        l1: obj.strategic_objective,
                        l2: obj.strategic_initiative,
                        l3: obj.operational_initiative
                      },
                      individual_objective: {
                        id: obj.id,
                        title: obj.title,
                        description: obj.description ?? null,
                        role_type: obj.role_type,
                        owner: obj.owner,
                        functional_area: obj.functional_area,
                        status: obj.status,
                        progress: obj.progress,
                        start_date: obj.start_date ?? null,
                        end_date: obj.end_date ?? null
                      },
                      // Include ALL KPIs for this objective in one entry
                      kpis: (obj.kpis || []).map((kpi: any) => ({
                        id: kpi.id,
                        name: kpi.name,
                        description: kpi.description || '',
                        target_value: kpi.target_value,
                        current_value: kpi.current_value || 0,
                        unit: kpi.unit,
                        status: kpi.status as 'on-track' | 'at-risk' | 'off-track',
                        frequency: kpi.frequency || 'monthly',
                        owner: memberName,
                        functional_area: obj.functional_area,
                        progress_percentage: undefined
                      }))
                    }))
                )}
                roleType={memberDetails.role}
                compact={true}
                onSelectKPI={(kpi) => {
                  if (import.meta.env.DEV) console.log('Selected KPI from member cascade:', kpi)
                  handleNavigateToKPI(kpi.id)
                }}
                onSelectObjective={(objective) => {
                  if (import.meta.env.DEV) console.log('Selected objective from member cascade:', objective)
                  // TODO: Open objective details
                }}
              />
            </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
      
      {/* Objective Configuration Dialog - for approving/managing pending objectives */}
      <ObjectiveConfigurationDialog
        open={showObjectiveConfig}
        onOpenChange={setShowObjectiveConfig}
        initialTab={objectiveConfigInitialTab}
        selectedYear={selectedYear}
        teamMember={{
          employee_id: memberId,
          display_name: memberName,
          job_title: propMemberRole,
          department: memberData?.department || '',
          campus: memberData?.campus || '',
          objectives: {
            total: displayKPAs.reduce((sum, kpa) => sum + (kpa.individualObjectives?.length || 0), 0),
            onTrack: 0,
            atRisk: 0,
            offTrack: 0,
            completed: 0,
            pending_approval: displayKPAs.reduce((sum, kpa) => 
              sum + (kpa.individualObjectives?.filter((obj: any) => obj.status === 'pending_approval' || obj.status === 'pending-approval').length || 0), 0),
            progress: 0
          },
          pending_objectives: displayKPAs.flatMap(kpa => 
            (kpa.individualObjectives || []).filter((obj: any) => obj.status === 'pending_approval' || obj.status === 'pending-approval')
          ),
          active_objectives: displayKPAs.flatMap(kpa => 
            (kpa.individualObjectives || []).filter((obj: any) => obj.status === 'active')
          ),
          rejected_objectives: displayKPAs.flatMap(kpa => 
            (kpa.individualObjectives || []).filter((obj: any) => obj.status === 'rejected')
          )
        }}
        userRole="manager"
        onApprove={handleApproveObjective}
        onReject={handleRejectObjective}
        onUpdate={handleUpdateObjectiveConfig}
        onAllocate={handleAllocateObjectives}
        onObjectivesAdded={async () => {
          // Objectives copied in from the year before or the Objective Library (HR UAT list #27)
          await ensureCollaborativeSession('objective_setting')
          await loadCollaborativeSessions()
          await onRefreshEvidence?.()
        }}
        onObjectivesChange={onRefreshEvidence}
        onSessionChange={loadCollaborativeSessions}
      />
    </motion.div>,
    document.body
  )
}
